import React, { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';

import { createRoot, type Root } from 'react-dom/client';

import { getDocument } from '@atlaskit/browser-apis';

import { StatusAnnouncement } from './status-announcement';
import statusAnnouncementStore, {
	type Announcement,
	type AnnouncementOwner,
} from './status-announcement-store';

let root: Root | null = null;

/**
 * Client-only fallback for apps without the app-level provider; the provider's
 * server-rendered status element prevents this second live region from mounting.
 */
function GlobalStatusAnnouncement(): React.JSX.Element {
	const text = useSyncExternalStore(
		statusAnnouncementStore.subscribe,
		statusAnnouncementStore.getSnapshot,
		statusAnnouncementStore.getServerSnapshot,
	);
	return <StatusAnnouncement>{text}</StatusAnnouncement>;
}

function ensureStatusAnnouncement(): void {
	const doc = getDocument();
	if (
		!doc ||
		root !== null ||
		doc.querySelector('[data-entry-point-status-announcement]') !== null
	) {
		return;
	}

	const container = doc.createElement('div');
	container.dataset.entryPointStatusAnnouncement = 'true';
	doc.body.appendChild(container);
	root = createRoot(container);
	root.render(<GlobalStatusAnnouncement />);
}

/**
 * Publishes entry-point status messages through the page's single persistent
 * live region. The live region communicates loading and error states to assistive
 * technology. It mounts on page load so later text changes are announced reliably.
 */
export function useStatusAnnouncement(): {
	activateStatusAnnouncement: () => void;
	announce: (announcement: Announcement) => void;
	cancelPendingAnnouncement: () => boolean;
	resetStatus: () => void;
} {
	const ownerRef = useRef<AnnouncementOwner | null>(null);
	if (ownerRef.current === null) {
		ownerRef.current = Symbol('status-announcement-owner');
	}
	const owner = ownerRef.current;

	useEffect(() => {
		ensureStatusAnnouncement();
		return () => {
			statusAnnouncementStore.resetStatus(owner);
		};
	}, [owner]);

	const activateStatusAnnouncement = useCallback(() => {
		ensureStatusAnnouncement();
	}, []);
	const announce = useCallback(
		(announcement: Announcement) => statusAnnouncementStore.announce(owner, announcement),
		[owner],
	);
	const cancelPendingAnnouncement = useCallback(
		() => statusAnnouncementStore.cancelPendingAnnouncement(owner),
		[owner],
	);
	const resetStatus = useCallback(() => {
		statusAnnouncementStore.resetStatus(owner);
	}, [owner]);

	return {
		activateStatusAnnouncement,
		announce,
		cancelPendingAnnouncement,
		resetStatus,
	};
}
